import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, money } from "@/lib/api";
import { DepositOffer } from "@/components/DepositOffer";
import { getAttribution, getPartnerRef } from "@/lib/attribution";

const EMPTY = { name: "", email: "", company: "", notes: "", website: "" };
const field = "bg-slate-950/60 border-slate-700";

export const PricingRequestDialog = ({ tier, tiers, currency, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [sel, setSel] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [deposit, setDeposit] = useState(null);
  const [statusToken, setStatusToken] = useState(null);
  useEffect(() => { if (tier) { setSel(tier); setDone(false); setDeposit(null); } }, [tier]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await api.post("/public/pricing-requests", { ...form, tier: sel, ref: getPartnerRef(), attribution: getAttribution() }); setDeposit(r.data.deposit); setStatusToken(r.data.status_token); setDone(true); setForm(EMPTY); }
    catch (err) { toast.error(errMsg(err, "Could not send your request")); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!tier} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-lg" data-testid="pricing-request-dialog">
        {done ? (
          <div className="py-6 text-center" data-testid="pricing-request-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
            <p className="font-display text-xl font-bold mb-1">Request received</p>
            <p className="text-sm text-slate-400 mb-2">Thanks! Our team will email you shortly to talk about your project.</p>
            {statusToken && <a href={`/status/${statusToken}`} data-testid="pricing-track-link" className="inline-block text-sm text-emerald-400 underline mb-6">Track my request</a>}
            {deposit ? <DepositOffer deposit={deposit} onSkip={onClose} />
              : <Button data-testid="pricing-request-close" onClick={onClose} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">Done</Button>}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="pricing-request-form">
            <DialogHeader><DialogTitle className="font-display">Get started</DialogTitle><DialogDescription>Tell us about your project and we'll be in touch.</DialogDescription></DialogHeader>
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger data-testid="pricing-request-tier" className={field}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">{tiers.map((t) => <SelectItem key={t.key} value={t.key} data-testid={`pricing-request-tier-${t.key}`}>{t.label} · {money(t.price, currency)}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input data-testid="pricing-request-name" required value={form.name} onChange={set("name")} className={field} placeholder="Your name *" />
              <Input data-testid="pricing-request-email" required type="email" value={form.email} onChange={set("email")} className={field} placeholder="Email *" />
            </div>
            <Input data-testid="pricing-request-company" value={form.company} onChange={set("company")} className={field} placeholder="Company (optional)" />
            <Textarea data-testid="pricing-request-notes" rows={4} value={form.notes} onChange={set("notes")} className={field} placeholder="Project notes: the idea, address or franchise brands you're considering" />
            <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
            <Button type="submit" data-testid="pricing-request-submit" disabled={busy} className="w-full rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Send request</Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
